import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../../App";
import { useTimezone } from "../../../contexts/TimezoneContext";
import { hasFullAccess } from "../../../utils/permissions";
import { DATE_FILTER_OPTIONS } from "../../../utils/dateUtils";
import JobPreviewModal from "../../jobs/JobPreviewModal";
import ExpenseFormDialog from "../../expenses/ExpenseFormDialog";
import UsageCard from "../../UsageCard";
import { getAuthCfg } from "./singleUtils";
import CommandHeader from "./CommandHeader";
import KpiStrip from "./KpiStrip";
import AttentionStrip from "./AttentionStrip";
import OperationsBoard from "./OperationsBoard";
import ProfitPanel from "./ProfitPanel";
import FinChart from "./FinChart";
import ExpenseCenter from "./ExpenseCenter";
import CommsPanel from "./CommsPanel";
import TechBoard from "./TechBoard";
import InvoicePipeline from "./InvoicePipeline";
import ActivityLog, { InventoryCard } from "./ActivityLog";
import UpcomingTasksCard from "../UpcomingTasksCard";

const prefsKey = (user) => `single_dash_prefs_${user?.user_id || user?.id || "anon"}`;

const Skeleton = () => (
  <div className="space-y-4" data-testid="single-dashboard-skeleton">
    <div className="h-16 w-72 rounded-md bg-muted animate-pulse" />
    <div className="h-12 rounded-md bg-muted animate-pulse" />
    <div className="flex gap-2.5">
      {[...Array(6)].map((_, i) => <div key={i} className="h-[86px] flex-1 rounded-md bg-muted animate-pulse" />)}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 h-96 rounded-md bg-muted animate-pulse" />
      <div className="h-96 rounded-md bg-muted animate-pulse" />
    </div>
  </div>
);

// Single-Location Command Center — data source: /api/store/dashboard-v2
// (one shared backend metric definition for every KPI; see routers/store_dashboard.py)
const SingleDashboard = ({ user }) => {
  const navigate = useNavigate();
  const timezoneCtx = useTimezone();
  const getToday = timezoneCtx?.getToday || (() => new Date());

  const saved = (() => {
    try { return JSON.parse(localStorage.getItem(prefsKey(user)) || "{}"); } catch { return {}; }
  })();
  const [dateFilter, setDateFilter] = useState(saved.dateFilter || "today");
  const [customStart, setCustomStart] = useState(saved.customStart || "");
  const [customEnd, setCustomEnd] = useState(saved.customEnd || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [previewJob, setPreviewJob] = useState(null);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [sendingToTech, setSendingToTech] = useState(null);
  const [sendingReview, setSendingReview] = useState(null);
  const [live, setLive] = useState(false);
  const fetchDataRef = useRef(() => {});

  useEffect(() => {
    try { localStorage.setItem(prefsKey(user), JSON.stringify({ dateFilter, customStart, customEnd })); } catch { /* full */ }
  }, [dateFilter, customStart, customEnd, user]);

  const fetchData = useCallback(async (showLoader = true) => {
    if (dateFilter === "custom" && (!customStart || !customEnd)) return;
    if (showLoader && !data) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await axios.get(`${API}/store/dashboard-v2`, {
        ...getAuthCfg(),
        params: {
          date_filter: dateFilter,
          ...(dateFilter === "custom" ? { start_date: customStart, end_date: customEnd } : {}),
        },
      });
      setData(res.data);
      setError(null);
    } catch (e) {
      if (!data) setError(e.response?.data?.detail || e.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, customStart, customEnd]);

  useEffect(() => { fetchData(true); }, [fetchData]);
  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);

  // Backup polling — slower when the live socket is healthy.
  useEffect(() => {
    const id = setInterval(() => fetchData(false), live ? 300000 : 60000);
    return () => clearInterval(id);
  }, [fetchData, live]);

  // WebSocket live updates (payments, jobs, calls, messages, expenses).
  useEffect(() => {
    let ws, closed = false, retry = 0, debounceTimer, reconnectTimer;
    const connect = () => {
      const token = localStorage.getItem("token");
      if (!token || closed) return;
      try {
        ws = new WebSocket(`${API.replace(/^http/, "ws")}/ws/dashboard?token=${encodeURIComponent(token)}`);
      } catch { return; }
      ws.onopen = () => { retry = 0; setLive(true); };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type && msg.type !== "ping") {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => fetchDataRef.current(false), 1200);
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setLive(false);
        if (!closed) { retry += 1; reconnectTimer = setTimeout(connect, Math.min(30000, 2000 * retry)); }
      };
      ws.onerror = () => { try { ws.close(); } catch { /* closed */ } };
    };
    connect();
    return () => {
      closed = true;
      clearTimeout(debounceTimer);
      clearTimeout(reconnectTimer);
      try { ws && ws.close(); } catch { /* noop */ }
    };
  }, []);

  useEffect(() => {
    axios.get(`${API}/settings/payment-types`, getAuthCfg())
      .then((r) => setPaymentTypes(Array.isArray(r.data) ? r.data : r.data?.payment_types || []))
      .catch(() => {});
    axios.get(`${API}/expenses/categories`, getAuthCfg())
      .then((r) => setCategories(r.data?.categories || []))
      .catch(() => {});
  }, []);

  const openJob = async (row) => {
    try {
      const res = await axios.get(`${API}/jobs/${row.job_id}`, getAuthCfg());
      setPreviewJob(res.data);
    } catch { toast.error("Could not open job"); }
  };
  const handleSendReminder = async (job) => {
    try {
      await axios.post(`${API}/notifications/send-reminder`, {
        job_id: job.job_id, customer_phone: job.customer_phone, customer_name: job.customer_name,
        job_title: job.title, scheduled_date: job.scheduled_date, scheduled_time: job.scheduled_time,
      }, getAuthCfg());
      toast.success("Reminder sent");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send reminder"); }
  };
  const handleSendToTech = async (job) => {
    if (!job.assigned_to) { toast.error("No technician assigned to this job"); return; }
    setSendingToTech(job.job_id);
    try {
      const res = await axios.post(`${API}/technicians/jobs/${job.job_id}/send-assignment`, {}, getAuthCfg());
      if (res.data?.masking_warning) toast.warning(res.data.masking_warning, { duration: 9000 });
      toast.success("Job sent to technician");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send job"); }
    finally { setSendingToTech(null); }
  };
  const handleSendReviewRequest = async (job) => {
    setSendingReview(job.job_id);
    try {
      await axios.post(`${API}/jobs/${job.job_id}/send-review-request`, {}, getAuthCfg());
      toast.success("Review request sent");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send review request"); }
    finally { setSendingReview(null); }
  };
  const handleDelete = async (job) => {
    if (!window.confirm(`Delete job "${job.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/jobs/${job.job_id}`, getAuthCfg());
      toast.success("Job deleted");
      fetchData(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete job"); }
  };
  const handleRename = async (job, title) => {
    try {
      await axios.patch(`${API}/jobs/${job.job_id}/title`, { title }, getAuthCfg());
      setPreviewJob((prev) => (prev && prev.job_id === job.job_id ? { ...prev, title } : prev));
      toast.success("Job renamed");
      fetchData(false);
    } catch { toast.error("Failed to rename job"); }
  };

  if (loading && !data) return <Skeleton />;
  if (error && !data) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3" data-testid="single-dashboard-error">
        <p className="text-foreground font-semibold">Could not load dashboard</p>
        <p className="text-muted-foreground text-sm">{error}</p>
        <button onClick={() => fetchData(true)} className="px-4 py-2 rounded-md bg-amber-500 hover:bg-amber-400 text-black text-sm font-semibold">Retry</button>
      </div>
    );
  }

  const d = data || {};
  const fa = !!d.financial_access;
  let today;
  try { today = getToday(); } catch { today = new Date(); }
  const periodLabel = dateFilter === "custom" && customStart
    ? `${customStart} → ${customEnd}`
    : DATE_FILTER_OPTIONS.find((o) => o.value === dateFilter)?.label || "Today";

  return (
    <div className="space-y-4" data-testid="single-dashboard">
      <CommandHeader
        user={user} businessName={d.business_name} today={today} periodLabel={periodLabel}
        live={live} refreshing={refreshing}
        dateFilter={dateFilter} onDateFilterChange={setDateFilter}
        customStart={customStart} customEnd={customEnd}
        onCustomChange={({ start, end }) => { setCustomStart(start); setCustomEnd(end); }}
        onAddExpense={() => setExpenseOpen(true)} financialAccess={fa}
      />

      <AttentionStrip items={d.needs_attention} />

      <KpiStrip kpis={d.kpis} financial={d.financial} comms={d.communications} financialAccess={fa} loading={loading} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 min-w-0 space-y-4">
          <OperationsBoard operations={d.operations} onOpenJob={openJob} financialAccess={fa} />
          {fa && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <ProfitPanel financial={d.financial} onRetry={() => fetchData(true)} />
              <FinChart />
            </div>
          )}
          {fa && (
            <ExpenseCenter
              expenses={d.expenses} dateFilter={dateFilter}
              customStart={customStart} customEnd={customEnd}
              onAddExpense={() => setExpenseOpen(true)} onRetry={() => fetchData(true)}
            />
          )}
        </div>
        <div className="min-w-0 space-y-4">
          <UpcomingTasksCard variant="single" />
          <CommsPanel comms={d.communications} onRetry={() => fetchData(true)} />
          <TechBoard technicians={d.technicians} financialAccess={fa} />
          <InvoicePipeline outstanding={d.outstanding} documents={d.documents} financialAccess={fa} onRetry={() => fetchData(true)} />
          <InventoryCard inventory={d.inventory} />
          <ActivityLog activity={d.activity} financialAccess={fa} />
          {hasFullAccess(user) && (
            <div data-testid="plan-usage-section"><UsageCard /></div>
          )}
        </div>
      </div>

      {/* Quick Add Expense — dashboard totals refresh instantly after save */}
      <ExpenseFormDialog
        open={expenseOpen}
        onOpenChange={setExpenseOpen}
        editing={null}
        categories={categories}
        onSaved={() => fetchData(false)}
      />

      {previewJob && (
        <JobPreviewModal
          job={previewJob}
          onClose={() => { setPreviewJob(null); fetchData(false); }}
          onEdit={(job) => navigate(`/dashboard/jobs/${job.job_id}/edit`)}
          onDelete={handleDelete}
          onSendReminder={handleSendReminder}
          onSendToTech={handleSendToTech}
          onSendReviewRequest={handleSendReviewRequest}
          onNewJobForCustomer={(job) => navigate("/dashboard/jobs/new", { state: { customerPrefill: { customer_id: job.customer_id || "", customer_name: job.customer_name || "", customer_phone: job.customer_phone || "", customer_email: job.customer_email || "" } } })}
          onOpenCustomer={(job) => job.customer_id && navigate(`/dashboard/customers?open=${job.customer_id}`)}
          sendingToTech={sendingToTech}
          sendingReview={sendingReview}
          paymentTypes={paymentTypes}
          onRename={handleRename}
        />
      )}
    </div>
  );
};

export default SingleDashboard;
